import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
  Box,
  FormHelperText,
} from '@mui/material';
import { saveTenplate, updateTenplate } from './utils/templatesApi';
import { generatePreviewTemplate } from './utils/templatesApi';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import {
  getCategories,
  generateCount,
  updateLastActive,
} from './utils/templatesApi';
import { usersList } from './utils/templatesApi';
import CloseIcon from '@mui/icons-material/Close';
import * as Yup from 'yup';
import { Editor } from '@tinymce/tinymce-react';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const Template = ({
  template,
  open,
  onClose,
  onNewAdd,
  setReloadTrigger,
  role,
  userId,
}) => {
  const [isEdited, setIsEdited] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false); // controls preview modal
  const [text, setText] = useState('');
  const [variant, setVariant] = useState(null);
  const [generatedData, setGeneratedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [Options, setOptions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const validationSchema = Yup.object({
    name: Yup.string().required('Template name is required'),
    categoryId: Yup.string().required('Category name is required'),
    // subject: Yup.string().required('Subject is required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: validationSchema,
    initialValues: {
      name: template?.name || '',
      categoryId: Options?.some((opt) => opt.id === template?.categoryId)
        ? template.categoryId
        : '', // fallback if value not in options
      selectedUsers: template?.users?.map((user) => user.id) || [],
      subject: template?.subject || '',
    },
  });

  useEffect(() => {
    if (template?.users) {
      setSelectedUsers(template.users.map((user) => user.id));
    }
  }, [template]);

  const handleRemove = (userToRemove) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.filter((user) => user !== userToRemove),
    );
  };

  const id = template?.id;

  const categoriesList = async () => {
    const response = await getCategories();
    setOptions(response?.data);
  };

  const fetchUsers = async () => {
    try {
      const response = await usersList();
      const formattedUsers = response?.data?.map((user) => ({
        id: user.id,
        name: user.first_name,
      }));
      setUsers(formattedUsers);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  useEffect(() => {
    categoriesList();
    fetchUsers();
  }, []);

  const handleGenerate = async () => {
    if (!text.trim()) return;
    updateLastActive(userId, new Date());
    try {
      setLoading(true);
      const data = await generatePreviewTemplate(text);

      if (!data || !data.metaData || !data.spintax) {
        console.error('Invalid data returned:', data);
        return;
      }
      setLoading(false);
      setGeneratedData(data);
      setVariant(data.spintax);
    } catch (error) {
      console.error('Error generating preview:', error);
    }
  };

  function countSpintaxCombinationsDoubleBraces(spintax) {
    const regex = /\{\{([^{}]+?)\}\}/g;
    let match;
    let total = 1;

    while ((match = regex.exec(spintax)) !== null) {
      const options = match[1].split('|').map((s) => s.trim());
      total *= options.length;
    }
    return total;
  }

  const handleDirectSave = async () => {
    updateLastActive(userId, new Date());
    const totalTemplates = countSpintaxCombinationsDoubleBraces(text);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched({ name: true, categoryId: true });
      return;
    }

    if (!text.trim()) return;

    const extractedMetaData = extractMetaDataFromSpintax(text);

    const response = await saveTenplate({
      content: text,
      variants: text,
      metaData: extractedMetaData.join(','),
      categoryId: formik.values.categoryId,
      userList: selectedUsers,
      name: formik.values.name,
      subject: formik.values.subject,
      possibleTemplates: totalTemplates, // ✅ use it directly here
    });

    if (response.createdAt) {
      setReloadTrigger((prev) => !prev);
      toast.success('Template created successfully');
    } else {
      toast.error('Error in creating the Template');
    }

    onNewAdd();
    onClose();
  };

  const extractMetaDataFromSpintax = (spintax) => {
    const regex = /{([^{}]+)}/g;
    const matches = [...spintax.matchAll(regex)];
    return matches.map((match) => match[1]);
  };

  const handleSubmit = async () => {
    updateLastActive(userId, new Date());
    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      formik.setTouched({ name: true, categoryId: true }); // mark field as touched to show error
      return; // don't proceed
    }
    if (id) {
      const totalTemplates = countSpintaxCombinationsDoubleBraces(text);
      const data = {
        content: text,
        variants: text,
        metaData: generatedData?.metaData?.join(',') || '',
        categoryId: formik.values.categoryId,
        userList: selectedUsers,
        name: formik.values.name,
        subject: formik.values.subject,
        possibleTemplates: totalTemplates
      };

      const response = await updateTenplate(data, id);
      if (response?.success) {
        setReloadTrigger((prev) => !prev);
        toast.success('Template updated successfully');
      } else {
        toast.error('Error in updating the Template');
      }
    } else {
      const response = await saveTenplate({
        content: isEdited ? text : generatedData.template,
        variants: isEdited ? text : generatedData.spintax,
        metaData: generatedData?.metaData?.join(',') || '',
        categoryId: formik.values.categoryId,
        userList: selectedUsers,
        name: formik.values.name,
        subject: formik.values.subject,
        possibleTemplates: generatedData?.possibleTemplates,
      });
      if (response.createdAt) {
        setReloadTrigger((prev) => !prev);
        toast.success('Template created successfully');
      } else {
        toast.error('Error in creating the Template');
      }
    }

    onNewAdd();
    onClose();
  };

  useEffect(() => {
    setText(template?.content || '');

    if (template?.variants && template?.metaData) {
      setGeneratedData({
        template: template.content,
        spintax: template.variants,
        metaData: template.metaData.split(','),
      });
      setVariant(template.variants);
    }
  }, [template]);

  const parseSpintax = (spintax) => {
    return spintax.replace(
      /(\{\{([^{}]+)\}\}|\{([^{}]+)\})/g,
      (_, __, doubleGroup, singleGroup) => {
        const group = doubleGroup || singleGroup;
        const options = group.split('|').map((opt) => opt.trim());
        const randomOption =
          options[Math.floor(Math.random() * options.length)];
        return randomOption;
      },
    );
  };

  const handleGenerateEmail = async () => {
    if (text) {
      const emailTemplate = parseSpintax(text);
      const cleanedTemplate = emailTemplate.replace(
        /<p[^>]*>/g,
        '<p style="margin:0; line-height:1.5;">',
      );
      const data = {
        userId: userId,
        templateId: id,
        cleanedTemplate: cleanedTemplate,
      };
      if (id) {
        const response = await generateCount(data);
      }
      setVariant(cleanedTemplate);
      const blob = new Blob([cleanedTemplate], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob });
      try {
        await navigator.clipboard.write([clipboardItem]);
        toast.success('Copied to clipboard with formatting!');
      } catch (err) {
        console.error('Failed to copy: ', err);
        toast.error('Failed to copy');
      }
      setPreviewOpen(true);
    }
    updateLastActive(userId, new Date());
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            onClose();
          }
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h3" component="span" fontWeight={600}>
            Templates
          </Typography>
        </DialogTitle>

        {/* Scrollable content wrapper */}
        <DialogContent
          dividers
          sx={{
            maxHeight: '75vh', // Limits visible height and makes it scrollable
            overflowY: 'auto',
            px: 3,
          }}
        >
          {(role === 'super-admin' || role === 'admin') && (
            <>
              <FormControl margin="normal" sx={{ width: '50%', marginLeft: 3 }}>
                <TextField
                  fullWidth
                  label="Template Name"
                  name="name"
                  margin="normal"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  placeholder="Enter Template Name"
                />
                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  margin="normal"
                  value={formik.values.subject}
                  onChange={formik.handleChange}
                  // onBlur={formik.handleBlur}
                  // error={formik.touched.subject && Boolean(formik.errors.subject)}
                  // helperText={formik.touched.subject && formik.errors.subject}
                  placeholder="Enter Subject Name"
                />
              </FormControl>

              <Typography
                variant="h5"
                component="div"
                sx={{ mt: 2, marginLeft: 3 }}
              >
                Enter Your Template
              </Typography>
            </>
          )}
          <div style={{ marginLeft: '1.5rem' }}>
            {isLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 300,
                  left: 700,
                  zIndex: 10,
                  width: '100%',
                }}
              >
                <CircularProgress />
              </div>
            )}
            <Editor
              apiKey="vbjdy08xqijkrtq3dp2rk0ffuxsp4zugoxz9f8nqwa0mexr4"
              placeholder="Write your content here"
              value={text}
              onEditorChange={(content) => setText(content)}
              onInit={() => setIsLoading(false)}
              init={{
                height: 300,
                menubar: role === 'super-admin' || 'admin',
                statusbar: false,
                readonly: role !== 'admin' || role !== 'super-admin', // ✅ disable editing for non-admins
                forced_root_block: 'p', // ✅ restores normal paragraph flow

                plugins:
                  role === 'admin'
                    ? [
                      'advlist',
                      'autolink',
                      'lists',
                      'link',
                      'image',
                      'charmap',
                      'preview',
                      'anchor',
                      'searchreplace',
                      'visualblocks',
                      'code',
                      'fullscreen',
                      'insertdatetime',
                      'media',
                      'table',
                      'code',
                      'help',
                      'wordcount',
                    ]
                    : [],

                paste_as_text: false, // ✅ allow HTML structure (like <h1>, <h2>)
                paste_remove_styles: true, // optional: keep true if you only want to strip inline styles
                paste_remove_spans: true,
                paste_auto_cleanup_on_paste: true,

                toolbar:
                  role === 'admin' || role === 'super-admin'
                    ? 'undo redo | blocks | bold italic forecolor | ' +
                    'alignleft aligncenter alignright alignjustify | ' +
                    'bullist numlist outdent indent | removeformat | help'
                    : false,

                content_style: `
              body {
                font-family: Helvetica, Arial, sans-serif;
                font-size: 14px;
                line-height: 1.4;
                margin: 0;
                padding: 50px;
              }
              h1, h2, h3, h4, h5, h6 {
                font-weight: bold;
                margin: 0 0 10px 0;
              }
              p, div {
                margin: 0;
                padding: 0;
                line-height: 1.4;
              }
              br {
                line-height: 1.4;
              }
            `,
              }}
            />
          </div>

          {(role === 'super-admin' || role === 'admin') && (
            <>
              <Typography
                variant="h5"
                component="div"
                sx={{ mt: 4, marginLeft: 3 }}
              >
                Select Category
              </Typography>

              <FormControl
                margin="normal"
                sx={{ width: '50%', marginLeft: 3 }}
                error={
                  formik.touched.categoryId && Boolean(formik.errors.categoryId)
                }
              >
                <InputLabel id="category">Category</InputLabel>
                <Select
                  labelId="category"
                  id="category"
                  name="categoryId"
                  label="Category"
                  value={formik.values.categoryId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {Options?.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.category_name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.categoryId && formik.errors.categoryId && (
                  <FormHelperText>{formik.errors.categoryId}</FormHelperText>
                )}
              </FormControl>

              <Typography
                variant="h5"
                component="div"
                sx={{ mt: 4, marginLeft: 3 }}
              >
                Select Users to assign template
              </Typography>

              <FormControl margin="normal" sx={{ width: '50%', marginLeft: 3 }}>
                <InputLabel id="users-label">Users</InputLabel>
                <Select
                  labelId="users-label"
                  id="users"
                  multiple
                  value={selectedUsers}
                  onChange={(e) =>
                    setSelectedUsers(
                      typeof e.target.value === 'string'
                        ? e.target.value.split(',')
                        : e.target.value,
                    )
                  }
                  input={<OutlinedInput label="Users" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((userId) => {
                        const user = users.find((u) => u.id === userId);
                        return (
                          <Chip
                            key={userId}
                            label={user ? user.name : userId}
                            onMouseDown={(e) => e.stopPropagation()}
                            onDelete={() => handleRemove(userId)}
                            deleteIcon={<CloseIcon />}
                            sx={{ cursor: 'pointer' }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {users?.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          {(role !== 'admin' || role !== 'super-admin') && (
            <Typography
              variant="h6"
              component="div"
              sx={{ maxHeight: '300px', overflowY: 'auto', mt: 4 }}
              dangerouslySetInnerHTML={{ __html: variant }}
            />
          )}
          {!id && (
            <>
              {loading ? (
                <CircularProgress sx={{ mt: 4 }} />
              ) : (
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ maxHeight: '300px', overflowY: 'auto', mt: 4 }}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={(e) => {
                    setText(e.currentTarget.innerHTML);
                    setIsEdited(true);
                  }}
                >
                  {variant}
                </Typography>
              )}
            </>
          )}
        </DialogContent>

        {/* Actions stay fixed at the bottom */}
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined" color="secondary">
            Cancel
          </Button>

          {(role === 'super-admin' || role === 'admin') ? (
            id ? (
              <>
                <Button
                  onClick={handleSubmit}
                  variant="outlined"
                  color="primary"
                >
                  Update Template
                </Button>
                <Button
                  onClick={handleGenerateEmail}
                  variant="contained"
                  color="primary"
                >
                  Preview
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleDirectSave}
                  variant="outlined"
                  color="primary"
                >
                  Save Spintax
                </Button>
                <Button
                  onClick={handleGenerate}
                  variant="outlined"
                  color="primary"
                >
                  Generate with AI
                </Button>
                <Button
                  onClick={handleGenerateEmail}
                  variant="contained"
                  color="primary"
                >
                  Preview
                </Button>
                {!template.id && generatedData && (
                  <>
                    <Button
                      onClick={handleSubmit}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                  </>
                )}
              </>
            )
          ) : (
            <Button
              onClick={handleGenerateEmail}
              variant="contained"
              color="primary"
            >
              Generate Email
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false)
          updateLastActive(userId, new Date())
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle component="div">
          <Typography variant="h5">Generated Email Preview</Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div dangerouslySetInnerHTML={{ __html: variant }} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPreviewOpen(false)
              updateLastActive(userId, new Date())
            }}
            color="primary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Template;
